// ABOUTME: HTTP API client for Obsidian BI backend communication.
// ABOUTME: Handles authentication, error handling, and request/response formatting.

import type {
  InvestigationDetailResponse,
  InvestigationListResponse,
  CreateInvestigationRequest,
  CreateInvestigationResponse,
  SendMessageRequest,
  SendMessageResponse,
  Report,
  PrintReport,
  ReportSection,
  ReportListResponse,
  CreateReportRequest,
  UpdateReportRequest,
  MagicEditRequest,
  MagicEditResponse,
  PublishReportRequest,
  GenerateReportWithContextRequest,
  GenerationConfig,
  FullEditRequest,
  FullEditSectionSuggestion,
  FullReportEditSuggestion,
  DataSource,
  TestDataSourceRequest,
  TestDataSourceResponse,
  CreateDataSourceRequest,
  APIError,
  PaginationParams,
  UserProfile,
  UserPreferences,
  UpdateUserPreferencesRequest,
  UpdateVisualizationData,
  UpdateVisualizationOptions,
  UpdateVisualizationResponse,
  WorkspaceMetrics,
  LimitExceededError,
  OnboardingProgress,
  UpdateOnboardingProgressRequest,
  FeatureFlags,
  SharedReportResponse,
  ReportCommentRequest,
  ReportComment,
  ReportCommentReply,
  ReportCommentsResponse,
  ReportViewBeacon,
  ReportAnalyticsResponse,
  UpdateSharingConfigRequest,
  DomainPeersResponse,
  DismissDomainPromptResponse,
  DomainClustersResponse,
  UpdateTableDataOptions,
} from '@/types/api';

// API configuration
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
    public retryAfter?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Token getter function (set by AuthContext)
// Exported for use by XHR-based upload helpers that can't use apiFetch
export let getToken: (() => Promise<string>) | null = null;

// Workspace ID getter function (set by AppContext)
// Exported for use by XHR-based upload helpers that can't use apiFetch
export let getWorkspaceId: (() => string) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getToken = getter;
}

export function setWorkspaceIdGetter(getter: () => string) {
  getWorkspaceId = getter;
}

// Base fetch wrapper with auth and error handling
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth token
  let token: string | null = null;
  if (getToken) {
    try {
      token = await getToken();
    } catch {
      // Token acquisition failed — request will proceed unauthenticated
    }
  }

  // Get current workspace ID
  let workspaceId: string | null = null;
  if (getWorkspaceId) {
    workspaceId = getWorkspaceId();
  }

  // Build headers
  // Don't send X-Workspace-ID to /users/me endpoint (user info is workspace-independent)
  // Don't set Content-Type for FormData — the browser sets it with the boundary
  const isUserMeEndpoint = /\/users\/me(\/|$|\?)/.test(url);
  const isFormData = options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!isUserMeEndpoint && workspaceId && workspaceId !== 'ws_default' ? { 'X-Workspace-ID': workspaceId } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle non-OK responses
  if (!response.ok) {
    let errorData: APIError;
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        error: 'unknown_error',
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Handle 403 workspace access errors - reset to primary workspace
    if (response.status === 403 && errorData.detail?.includes('workspace')) {
      console.error('[API Client] Workspace access denied, resetting to primary');
      // Dispatch custom event to trigger workspace reset
      window.dispatchEvent(new CustomEvent('workspace-access-denied'));
    }

    // Handle 402 limit exceeded errors - dispatch event to show upgrade modal
    if (response.status === 402 && errorData.error === 'limit_exceeded') {
      console.warn('[API Client] Usage limit exceeded:', errorData);
      const limitError = errorData as unknown as LimitExceededError;
      // Validate required fields before dispatching event
      if (
        limitError.resource &&
        typeof limitError.used === 'number' &&
        typeof limitError.limit === 'number'
      ) {
        window.dispatchEvent(
          new CustomEvent('limit-exceeded', {
            detail: {
              resource: limitError.resource,
              used: limitError.used,
              limit: limitError.limit,
              resetsAt: limitError.resets_at,
            },
          })
        );
      } else {
        console.error('[API Client] Invalid limit exceeded response structure:', errorData);
      }
    }

    throw new ApiError(
      response.status,
      errorData.error || 'api_error',
      errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
      errorData.details,
      errorData.request_id,
      errorData.retry_after
    );
  }

  // Handle empty responses
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Investigation API
export const investigationApi = {
  list: (params?: PaginationParams): Promise<InvestigationListResponse> => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return apiFetch(`/investigations${queryString ? `?${queryString}` : ''}`);
  },

  get: (id: string): Promise<InvestigationDetailResponse> => {
    return apiFetch(`/investigations/${id}`);
  },

  create: (data: CreateInvestigationRequest): Promise<CreateInvestigationResponse> => {
    return apiFetch('/investigations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  sendMessage: (id: string, data: SendMessageRequest): Promise<SendMessageResponse> => {
    return apiFetch(`/investigations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: (id: string): Promise<void> => {
    return apiFetch(`/investigations/${id}`, {
      method: 'DELETE',
    });
  },

  rename: (id: string, title: string): Promise<void> => {
    return apiFetch(`/investigations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },

  getSuggestedPrompts: (id: string): Promise<{
    prompts: string[];
    data_source_id: string | null;
    data_source_name: string | null;
  }> => {
    return apiFetch(`/investigations/${id}/suggested-prompts`);
  },

  getWorkspaceSuggestedPrompts: (): Promise<{
    prompts: string[];
    data_source_id: string | null;
    data_source_name: string | null;
  }> => {
    return apiFetch('/investigations/suggested-prompts');
  },
};

// Report API
export const reportApi = {
  list: (params?: PaginationParams & { workspace_id?: string }): Promise<ReportListResponse> => {
    const query = new URLSearchParams();
    if (params?.workspace_id) query.set('workspace_id', params.workspace_id);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return apiFetch(`/reports${queryString ? `?${queryString}` : ''}`);
  },

  get: (id: string): Promise<Report> => {
    return apiFetch(`/reports/${id}`);
  },

  getWithPrintToken: async (id: string, token: string): Promise<PrintReport> => {
    // Use raw fetch (not apiFetch) to avoid triggering Auth0 token getter
    // The token in the URL provides authentication, not the Authorization header
    const url = `${API_BASE_URL}/reports/${id}/print-with-token?token=${token}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'unknown', message: 'Unknown error' }));
      throw new ApiError(
        response.status,
        errorData.error || 'api_error',
        errorData.message || `HTTP ${response.status}`,
        errorData.details
      );
    }

    return response.json();
  },

  create: (data: CreateReportRequest): Promise<Report> => {
    return apiFetch('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  generateWithContext: (data: GenerateReportWithContextRequest): Promise<Report> => {
    return apiFetch('/reports/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (id: string, data: UpdateReportRequest, version: number): Promise<Report> => {
    return apiFetch(`/reports/${id}`, {
      method: 'PATCH',
      headers: {
        'If-Match': String(version),
      },
      body: JSON.stringify(data),
    });
  },

  updateSection: (
    reportId: string,
    sectionId: string,
    content: string,
    workspaceId: string,
    version: number
  ): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/sections/${sectionId}`, {
      method: 'PATCH',
      headers: {
        'If-Match': String(version),
      },
      body: JSON.stringify({
        section_id: sectionId,
        new_content: content,
        workspace_id: workspaceId,
      }),
    });
  },

  addSection: (
    reportId: string,
    section: ReportSection,
    editedBy: string,
    version: number
  ): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/sections`, {
      method: 'POST',
      headers: {
        'If-Match': String(version),
      },
      body: JSON.stringify({
        section,
        edited_by: editedBy,
      }),
    });
  },

  deleteSection: (
    reportId: string,
    sectionId: string,
    editedBy: string,
    version: number
  ): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/sections/${sectionId}?edited_by=${editedBy}`, {
      method: 'DELETE',
      headers: {
        'If-Match': String(version),
      },
    });
  },

  reorderSections: (
    reportId: string,
    sectionOrder: string[],
    editedBy: string,
    version: number
  ): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/sections/order`, {
      method: 'PUT',
      headers: {
        'If-Match': String(version),
      },
      body: JSON.stringify({
        section_order: sectionOrder,
        edited_by: editedBy,
      }),
    });
  },

  magicEdit: (id: string, data: MagicEditRequest): Promise<MagicEditResponse> => {
    return apiFetch(`/reports/${id}/magic-edit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  publish: (id: string, data: PublishReportRequest): Promise<Report> => {
    return apiFetch(`/reports/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getPdf: async (id: string): Promise<Blob> => {
    const url = `${API_BASE_URL}/reports/${id}/pdf`;
    let token: string | null = null;
    if (getToken) {
      token = await getToken();
    }

    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new ApiError(
        response.status,
        'pdf_generation_failed',
        'Failed to generate PDF'
      );
    }

    return response.blob();
  },

  delete: (id: string): Promise<void> => {
    return apiFetch(`/reports/${id}`, {
      method: 'DELETE',
    });
  },

  updateVisualization: (
    reportId: string,
    sectionId: string,
    data: UpdateVisualizationData,
    options?: UpdateVisualizationOptions
  ): Promise<UpdateVisualizationResponse> => {
    const headers: HeadersInit = {};
    if (options?.ifMatch !== undefined) {
      headers['If-Match'] = String(options.ifMatch);
    }

    return apiFetch(`/reports/${reportId}/sections/${sectionId}/visualization`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
  },

  updateTableData: (
    reportId: string,
    sectionId: string,
    tableData: Record<string, unknown>,
    options?: UpdateTableDataOptions
  ): Promise<UpdateVisualizationResponse> => {
    const headers: HeadersInit = {};
    if (options?.ifMatch !== undefined) {
      headers['If-Match'] = String(options.ifMatch);
    }

    return apiFetch(`/reports/${reportId}/sections/${sectionId}/table-data`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ table_data: tableData }),
    });
  },

  updateTableExpanded: (
    reportId: string,
    sectionId: string,
    expanded: boolean,
    options?: { ifMatch?: number }
  ): Promise<UpdateVisualizationResponse> => {
    const headers: HeadersInit = {};
    if (options?.ifMatch !== undefined) {
      headers['If-Match'] = String(options.ifMatch);
    }

    return apiFetch(`/reports/${reportId}/sections/${sectionId}/table-expanded`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ expanded }),
    });
  },

  getComments: (reportId: string): Promise<ReportCommentsResponse> => {
    return apiFetch(`/reports/${reportId}/comments`);
  },

  markCommentRead: (reportId: string, commentId: string): Promise<void> => {
    return apiFetch(`/reports/${reportId}/comments/${commentId}/read`, {
      method: 'PATCH',
    });
  },

  replyToComment: (
    reportId: string,
    commentId: string,
    content: string
  ): Promise<ReportCommentReply> => {
    return apiFetch(`/reports/${reportId}/comments/${commentId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  getAnalytics: (reportId: string): Promise<ReportAnalyticsResponse> => {
    return apiFetch(`/reports/${reportId}/analytics`);
  },

  updateSharing: (
    reportId: string,
    data: UpdateSharingConfigRequest
  ): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  revokeSharing: (reportId: string): Promise<void> => {
    return apiFetch(`/reports/${reportId}/publish`, {
      method: 'DELETE',
    });
  },

  getGenerationConfig: async (reportId: string, workspaceId: string): Promise<GenerationConfig | null> => {
    const raw = await apiFetch<any>(`/reports/${reportId}/generation-config?workspace_id=${workspaceId}`);
    if (!raw) return null;
    // Transform backend GenerationConfig → frontend GenerationConfig
    const reportType = raw.report_type || 'technical';
    const audience = raw.audience || 'c-suite';
    const focusAreas: string[] = raw.focus_areas || [];
    return {
      title: raw.title || '',
      custom_prompt: raw.custom_prompt || undefined,
      length: reportType === 'executive' ? '1-2' : reportType === 'detailed' ? '6+' : '3-5',
      tone: audience === 'c-suite' ? 'executive' : audience === 'technical' ? 'technical' : 'conversational',
      include_executive_summary: raw.custom_prompt?.toLowerCase().includes('executive summary') ?? true,
      include_methodology: raw.include_methodology ?? false,
      include_recommendations: focusAreas.includes('recommendations'),
      include_visuals: raw.include_charts ?? true,
      mandatory_insight_refs: raw.mandatory_insight_refs || [],
      investigation_ids: (raw.investigation_ids || []).map((id: any) => String(id)),
    };
  },

  regenerateReport: (reportId: string, config: Record<string, unknown> & { workspace_id: string; version?: number }): Promise<Report> => {
    const headers: Record<string, string> = {};
    if (config.version != null) {
      headers['If-Match'] = String(config.version);
    }
    return apiFetch(`/reports/${reportId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(config),
      headers,
    });
  },

  requestFullEdit: async (reportId: string, request: FullEditRequest): Promise<FullReportEditSuggestion> => {
    const raw = await apiFetch<FullReportEditSuggestion>(`/reports/${reportId}/full-edit`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const deletedIds = new Set<string>(raw.deleted_section_ids || []);
    // Transform backend response into section diff format for the UI
    const sections: FullEditSectionSuggestion[] = (raw.original_sections || []).map((orig: any, i: number) => {
      const sectionId = orig.id || `section-${i}`;
      if (deletedIds.has(orig.id)) {
        return {
          section_id: sectionId,
          changed: true,
          deleted: true,
          original: { id: sectionId, type: orig.type, content: orig.content || '', order: orig.order ?? i },
          suggested: { id: sectionId, type: orig.type, content: '', order: orig.order ?? i },
        };
      }
      const sugg = (raw.suggested_sections as any[])?.find((s: any) => s.id === orig.id) || orig;
      const origContent = orig.content || '';
      const suggContent = sugg.content || '';
      const origOrder = orig.order ?? i;
      const suggOrder = sugg.order ?? i;
      const reordered = origOrder !== suggOrder;
      const contentChanged = origContent !== suggContent;
      return {
        section_id: sectionId,
        changed: contentChanged,
        reordered,
        original: { id: sectionId, type: orig.type, content: origContent, order: origOrder },
        suggested: { id: sectionId, type: sugg.type, content: suggContent, order: suggOrder },
      };
    });

    // Detect new sections (in suggested but not in originals)
    const originalIds = new Set((raw.original_sections || []).map((s: any) => s.id));
    for (const sugg of (raw.suggested_sections || []) as any[]) {
      if (!originalIds.has(sugg.id)) {
        sections.push({
          section_id: sugg.id,
          changed: true,
          added: true,
          original: { id: sugg.id, type: sugg.type, content: '', order: sugg.order ?? 0 },
          suggested: { id: sugg.id, type: sugg.type, content: sugg.content || '', order: sugg.order ?? 0 },
        });
      }
    }

    return { ...raw, sections };
  },

  replaceSections: (reportId: string, sections: ReportSection[], workspaceId: string, version: number): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/sections`, {
      method: 'PUT',
      headers: { 'If-Match': String(version) },
      body: JSON.stringify({ sections, workspace_id: workspaceId }),
    });
  },

  applyFullEdit: (reportId: string, suggestedSections: ReportSection[], prompt: string, version: number): Promise<Report> => {
    return apiFetch(`/reports/${reportId}/full-edit/apply`, {
      method: 'POST',
      headers: { 'If-Match': String(version) },
      body: JSON.stringify({ suggested_sections: suggestedSections, prompt }),
    });
  },

  addInsightsToReport: (reportId: string, insightIds: string[], workspaceId: string, version: number, insertAtOrder?: number): Promise<Report> => {
    const payload: Record<string, unknown> = { workspace_id: workspaceId, insight_ids: insightIds };
    if (insertAtOrder != null) {
      payload.insert_at_order = insertAtOrder;
    }
    return apiFetch(`/reports/${reportId}/add-insights`, {
      method: 'POST',
      headers: { 'If-Match': String(version) },
      body: JSON.stringify(payload),
    });
  },
};

// Data Source API
export const dataSourceApi = {
  list: (): Promise<DataSource[]> => {
    return apiFetch('/data-sources');
  },

  get: (id: string): Promise<DataSource> => {
    return apiFetch(`/data-sources/${id}`);
  },

  create: (data: CreateDataSourceRequest): Promise<DataSource> => {
    return apiFetch('/data-sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  test: (data: TestDataSourceRequest): Promise<TestDataSourceResponse> => {
    return apiFetch('/data-sources/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: (id: string): Promise<void> => {
    return apiFetch(`/data-sources/${id}`, {
      method: 'DELETE',
    });
  },
};

// Admin API
export const adminApi = {
  listWorkspaces: (params?: PaginationParams) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const queryString = query.toString();
    return apiFetch(`/admin/workspaces${queryString ? `?${queryString}` : ''}`);
  },

  getWorkspaceUsage: (id: string) => {
    return apiFetch(`/admin/workspaces/${id}/usage`);
  },

  impersonate: (userId: string) => {
    return apiFetch('/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  },

  getAgentConfig: (workspaceId: string) => {
    return apiFetch(`/admin/workspaces/${workspaceId}/agent-config`);
  },

  updateAgentConfig: (workspaceId: string, config: unknown) => {
    return apiFetch(`/admin/workspaces/${workspaceId}/agent-config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  getDomainClusters: (params?: {
    min_users?: number;
    search?: string;
    include_consumer?: boolean;
  }): Promise<DomainClustersResponse> => {
    const query = new URLSearchParams();
    if (params?.min_users) query.set('min_users', String(params.min_users));
    if (params?.search) query.set('search', params.search);
    if (params?.include_consumer) query.set('include_consumer', 'true');
    const queryString = query.toString();
    return apiFetch(`/admin/domain-clusters${queryString ? `?${queryString}` : ''}`);
  },
};

// User API
export const userApi = {
  me: (): Promise<UserProfile> => {
    return apiFetch('/users/me');
  },

  updatePreferences: (data: UpdateUserPreferencesRequest): Promise<UserPreferences> => {
    return apiFetch('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateProfile: (data: { name?: string }): Promise<{ id: string; email: string; name: string; avatar_url: string | null }> => {
    return apiFetch('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateOnboardingProgress: (data: UpdateOnboardingProgressRequest): Promise<OnboardingProgress> => {
    return apiFetch('/users/me/onboarding', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getDomainPeers: (): Promise<DomainPeersResponse> => {
    return apiFetch('/users/me/domain-peers');
  },

  dismissDomainPrompt: (): Promise<DismissDomainPromptResponse> => {
    return apiFetch('/users/me/dismiss-domain-prompt', {
      method: 'POST',
    });
  },
};

// Workspace Metrics API
export const workspaceMetricsApi = {
  get: (): Promise<WorkspaceMetrics> => {
    return apiFetch('/workspace-metrics');
  },
};

// Features API (public — no auth required)
export const featuresApi = {
  getFlags: async (): Promise<FeatureFlags> => {
    const response = await fetch(`${API_BASE_URL}/features`);
    if (!response.ok) throw new Error('Failed to fetch feature flags');
    return response.json();
  },
};

// Shared Report API (public, no auth)
export const sharedReportApi = {
  get: async (shareId: string, options?: {
    password?: string;
    email?: string;
  }): Promise<SharedReportResponse> => {
    const url = `${API_BASE_URL}/shared/${shareId}`;

    // Password-protected reports use POST with the password in the body
    if (options?.password) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: options.password }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'unknown',
          message: 'Unknown error',
        }));
        throw new ApiError(
          response.status,
          errorData.error || errorData.detail || 'api_error',
          errorData.message || errorData.detail || `HTTP ${response.status}`,
          errorData.details,
          undefined,
          errorData.retry_after,
        );
      }
      return response.json();
    }

    // Public / email-gated reports use GET
    const params = new URLSearchParams();
    if (options?.email) params.set('viewer_email', options.email);
    const fullUrl = params.toString() ? `${url}?${params}` : url;
    const response = await fetch(fullUrl);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'unknown',
        message: 'Unknown error',
      }));
      throw new ApiError(
        response.status,
        errorData.error || errorData.detail || 'api_error',
        errorData.message || errorData.detail || `HTTP ${response.status}`,
        errorData.details,
        undefined,
        errorData.retry_after,
      );
    }
    return response.json();
  },

  submitComment: async (
    shareId: string,
    data: ReportCommentRequest
  ): Promise<ReportComment> => {
    const url = `${API_BASE_URL}/shared/${shareId}/comments`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'unknown',
        message: 'Unknown error',
      }));
      throw new ApiError(
        response.status,
        errorData.error || 'api_error',
        errorData.message || `HTTP ${response.status}`,
        errorData.details
      );
    }
    return response.json();
  },

  sendBeacon: (shareId: string, data: ReportViewBeacon): void => {
    const url = `${API_BASE_URL}/shared/${shareId}/views/beacon`;
    const blob = new Blob([JSON.stringify(data)], {
      type: 'application/json',
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true,
      }).catch(() => {
        // Intentionally swallowed -- beacon is best-effort
      });
    }
  },
};

// Export all APIs
export const api = {
  investigations: investigationApi,
  reports: reportApi,
  dataSources: dataSourceApi,
  admin: adminApi,
  users: userApi,
  workspaceMetrics: workspaceMetricsApi,
  features: featuresApi,
  sharedReports: sharedReportApi,
};
